import { useState } from "react";
import { Avatar, Button, Dialog } from "@dfws/ui";
import type { ChatChannel } from "@dfws/contracts";
import { ApiFailure } from "../../lib/api";
import { createGroup, nameOf, updateChannel, useChatPeople } from "./chatData";
import s from "./Chat.module.css";

function PeoplePicker({ selected, onToggle, exclude = [] }: { selected: Set<string>; onToggle: (email: string) => void; exclude?: string[] }) {
  const people = useChatPeople();
  const [q, setQ] = useState("");
  const list = (people.data?.items ?? []).filter((p) => !exclude.includes(p.email) && (p.name + " " + p.email).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className={s.picker}>
      <input className={s.field} type="search" placeholder="Search people" aria-label="Search people" value={q} onChange={(e) => setQ(e.target.value)} />
      <ul className={s.pickList} aria-label="People">
        {list.map((p) => (
          <li key={p.email}>
            <label className={s.pickRow}>
              <input type="checkbox" checked={selected.has(p.email)} onChange={() => onToggle(p.email)} />
              <Avatar name={p.name} email={p.email} size={24} presence={p.presence} />
              <span className={s.pickName}>{p.name}</span>
              <span className={s.pickEmail}>{p.email}</span>
            </label>
          </li>
        ))}
        {!list.length && <li className={s.pickEmpty}>{people.isPending ? "Loading people" : "No one matches"}</li>}
      </ul>
    </div>
  );
}

function useSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (e: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  return { selected, toggle };
}

export function NewGroupDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (c: ChatChannel) => void }) {
  const [name, setName] = useState("");
  const { selected, toggle } = useSelection();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    setError("");
    try {
      onCreated(await createGroup(name.trim(), [...selected]));
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : "The group could not be created.");
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="New group" description="Everyone you add can read the whole conversation, including messages from before they joined.">
      <form
        className={s.dialogForm}
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <label className={s.label}>
          Group name
          <input className={s.field} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="For example, Odoo team" required />
        </label>
        <PeoplePicker selected={selected} onToggle={toggle} />
        {error && <p className={s.formError} role="alert">{error}</p>}
        <div className={s.dialogActions}>
          <span className={s.selectedCount}>{selected.size ? `${selected.size} selected` : "Choose at least one person"}</span>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" loading={busy} disabled={!name.trim() || selected.size === 0}>Create group</Button>
        </div>
      </form>
    </Dialog>
  );
}

export function RenameDialog({ channel, onClose }: { channel: ChatChannel; onClose: () => void }) {
  const [name, setName] = useState(channel.name);
  const [topic, setTopic] = useState(channel.topic);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Group details">
      <form
        className={s.dialogForm}
        onSubmit={(e) => {
          e.preventDefault();
          void updateChannel(channel.id, { name: name.trim(), topic: topic.trim() }).then(onClose);
        }}
      >
        <label className={s.label}>
          Name
          <input className={s.field} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label className={s.label}>
          Topic <small>optional</small>
          <input className={s.field} value={topic} maxLength={250} onChange={(e) => setTopic(e.target.value)} placeholder="What this group is for" />
        </label>
        <div className={s.dialogActions}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!name.trim()}>Save</Button>
        </div>
      </form>
    </Dialog>
  );
}

export function MembersDialog({ channel, me, onClose }: { channel: ChatChannel; me: string; onClose: () => void }) {
  const people = useChatPeople();
  const { selected, toggle } = useSelection();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`People in ${channel.name}`}>
      <ul className={s.memberList}>
        {channel.members.map((m) => (
          <li key={m} className={s.pickRow}>
            <Avatar name={nameOf(m, people.data)} email={m} size={24} />
            <span className={s.pickName}>{m === me ? "You" : nameOf(m, people.data)}</span>
            <span className={s.pickEmail}>{m}</span>
          </li>
        ))}
      </ul>
      <h3 className={s.subhead}>Add people</h3>
      <PeoplePicker selected={selected} onToggle={toggle} exclude={channel.members} />
      <div className={s.dialogActions}>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button variant="primary" disabled={selected.size === 0} onClick={() => void updateChannel(channel.id, { addMembers: [...selected] }).then(onClose)}>
          Add {selected.size || ""}
        </Button>
      </div>
    </Dialog>
  );
}
